import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import type { CompareResponse, CompareResult } from "../lib/types";
import { fmtNumber } from "../lib/format";

export function ComparePage() {
  const [name, setName] = useState("");
  const [baseline, setBaseline] = useState<string | null>(null);

  const probe = useMutation({
    mutationFn: (q: string) =>
      api<CompareResponse>("/v1/foods/debug/compare", {
        method: "POST",
        body: JSON.stringify({ name: q }),
      }),
    onSuccess: (data) => {
      const first = data.results.find((r) => r.candidate);
      setBaseline(first?.source ?? null);
    },
  });

  const data = probe.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Source compare
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Probes every configured source in parallel. Does not write to D1.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const q = name.trim();
          if (!q) return;
          probe.mutate(q);
        }}
        className="flex max-w-xl items-start gap-2"
      >
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Food name (e.g. pad thai)"
          autoComplete="off"
          spellCheck={false}
          className="flex-1"
        />
        <Button
          type="submit"
          isLoading={probe.isPending}
          loadingText="Probing"
          disabled={!name.trim()}
        >
          <Zap size={14} strokeWidth={2.25} className="mr-1.5" />
          Run probes
        </Button>
      </form>

      {probe.error && (
        <Callout title="Probe failed" variant="error">
          {(probe.error as Error).message}
        </Callout>
      )}

      {data && (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-50">
              Results for: {data.query}
            </h2>
            <ResultsTable rows={data.results} />
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-50">
              Ingredient diff
            </h2>
            <DiffView
              results={data.results}
              baseline={baseline}
              onBaselineChange={setBaseline}
            />
          </section>
        </>
      )}
    </div>
  );
}

function ResultsTable({ rows }: { rows: CompareResult[] }) {
  if (rows.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-gray-500 dark:text-gray-500">
          No probes ran.
        </p>
      </Card>
    );
  }
  return (
    <TableRoot>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Source</TableHeaderCell>
            <TableHeaderCell className="text-right">ms</TableHeaderCell>
            <TableHeaderCell>Timed out</TableHeaderCell>
            <TableHeaderCell>Validated</TableHeaderCell>
            <TableHeaderCell>Error</TableHeaderCell>
            <TableHeaderCell>Ingredients</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.source}>
              <TableCell>
                <Badge variant="neutral">{r.source}</Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtNumber(r.duration_ms)}
              </TableCell>
              <TableCell>
                {r.timed_out ? <Badge variant="error">yes</Badge> : "no"}
              </TableCell>
              <TableCell>
                {r.validated === true && <Badge variant="success">pass</Badge>}
                {r.validated === false && (
                  <span className="flex items-center gap-2">
                    <Badge variant="error">fail</Badge>
                    <span className="text-xs">{r.validation_reason ?? ""}</span>
                  </span>
                )}
                {r.validated === null && "—"}
              </TableCell>
              <TableCell className="max-w-[260px]">{r.error ?? "—"}</TableCell>
              <TableCell>
                {r.candidate ? (
                  <span className="text-sm">
                    {r.candidate.ingredients.length} ·{" "}
                    <span className="text-gray-500 dark:text-gray-500">
                      {r.candidate.ingredients.slice(0, 4).join(", ")}
                      {r.candidate.ingredients.length > 4 ? "…" : ""}
                    </span>
                  </span>
                ) : (
                  "no result"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableRoot>
  );
}

/**
 * Set-diff between the chosen baseline source and every other source that
 * returned a candidate. Names are normalised the same way the cache normaliser
 * would (lowercase + collapsed whitespace) so trivial casing differences don't
 * appear as diffs. Mirrors the consistency rule in CLAUDE.md: same
 * name_normalized → same ingredients within a source_version.
 */
function DiffView({
  results,
  baseline,
  onBaselineChange,
}: {
  results: CompareResult[];
  baseline: string | null;
  onBaselineChange: (s: string) => void;
}) {
  const sourcesWithCandidate = results.filter((r) => r.candidate);

  if (sourcesWithCandidate.length < 2) {
    return (
      <Card>
        <p className="text-center text-sm text-gray-500 dark:text-gray-500">
          Need at least 2 sources with candidates to diff.
        </p>
      </Card>
    );
  }

  const baselineRow =
    sourcesWithCandidate.find((r) => r.source === baseline) ??
    sourcesWithCandidate[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-500">
          Baseline:
        </span>
        {sourcesWithCandidate.map((r) => (
          <Button
            key={r.source}
            variant={r.source === baselineRow.source ? "primary" : "secondary"}
            onClick={() => onBaselineChange(r.source)}
            className="!px-2.5 !py-1 !text-xs"
          >
            {r.source}
          </Button>
        ))}
      </div>

      {sourcesWithCandidate
        .filter((r) => r.source !== baselineRow.source)
        .map((other) => (
          <DiffPair key={other.source} baseline={baselineRow} other={other} />
        ))}
    </div>
  );
}

function DiffPair({
  baseline,
  other,
}: {
  baseline: CompareResult;
  other: CompareResult;
}) {
  const { onlyA, both, onlyB } = useMemo(() => {
    const a = new Map<string, string>();
    for (const n of baseline.candidate?.ingredients ?? []) a.set(norm(n), n);
    const b = new Map<string, string>();
    for (const n of other.candidate?.ingredients ?? []) b.set(norm(n), n);
    const onlyA: string[] = [];
    const both: string[] = [];
    const onlyB: string[] = [];
    for (const [k, label] of a) {
      if (b.has(k)) both.push(label);
      else onlyA.push(label);
    }
    for (const [k, label] of b) {
      if (!a.has(k)) onlyB.push(label);
    }
    return { onlyA: onlyA.sort(), both: both.sort(), onlyB: onlyB.sort() };
  }, [baseline, other]);

  const total = onlyA.length + both.length + onlyB.length;
  const overlapPct = total === 0 ? 0 : Math.round((both.length / total) * 100);

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="neutral">{baseline.source}</Badge>
          <span className="text-gray-500 dark:text-gray-500">vs</span>
          <Badge variant="neutral">{other.source}</Badge>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-500">
          {overlapPct}% overlap · {both.length} shared
        </span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <DiffColumn
          title={`Only in ${baseline.source}`}
          variant="error"
          items={onlyA}
        />
        <DiffColumn title="In both" variant="success" items={both} />
        <DiffColumn
          title={`Only in ${other.source}`}
          variant="default"
          items={onlyB}
        />
      </div>
    </Card>
  );
}

function DiffColumn({
  title,
  variant,
  items,
}: {
  title: string;
  variant: "default" | "error" | "success";
  items: string[];
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Badge variant={variant}>{items.length}</Badge>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {title}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-500">—</p>
      ) : (
        <ul className="space-y-1">
          {items.map((n) => (
            <li key={n} className="text-xs text-gray-700 dark:text-gray-300">
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}
